import React, { useState } from 'react';

import { Skeleton, TextField } from '@gerdau/hefesto';

import TextTooltipInformation from './TextTooltipInformation';
import {
  Container,
  OrderNameWrapper,
  OrderDetailsValuesContainer,
  OrderItemWrapper,
  ItemText,
  OrderValuesContainer,
  Divider,
  ContainerErrorTaxs,
  SpanErrorTaxs,
  SpanRetryTaxs,
  SpanObsTaxs,
  ContainerErrorTextTaxs,
  SkeletonContainer,
  ReceiverSection,
  ReceiverSectionTitle,
  ReceiverAddressBox,
  ReceiverAddressLabel,
  ReceiverAddressValue
} from './styles';
import { GetFlag } from '../../../../utils/featureFlag';
import { ITaxesCalculateResponse } from '../../../../store/ducks/order-simulate-checkout-mvp/types';

interface OrderItemProps {
  title: string;
  value?: string;
  secondValue?: string;
  otherValues?: string | number;
  tooltipText?: string;
  placement?: 'bottomLeft' | 'topLeft';
  loading?: boolean;
  error?: boolean;
}

interface CheckoutOrderProps {
  orderDetails: OrderItemProps[];
  receiverDetails?: OrderItemProps[];
  summaryDetails?: OrderItemProps[];
  orderValues: OrderItemProps[];
  orderTax: OrderItemProps[] | null;
  updateCustomerId: (customerId: string) => void;
  orderPaymentDescription: string;
  onLoadTaxes: () => Promise<null | undefined>;
  receiverAddress?: string;
  taxesData: {
    loading: boolean;
    error: boolean;
    data: ITaxesCalculateResponse | undefined;
    retryCount: number;
  };
}

const OrderItemTitle = ({
  title,
  tooltipText,
  placement
}: Pick<OrderItemProps, 'title' | 'tooltipText' | 'placement'>) => {
  if (tooltipText) {
    return (
      <TextTooltipInformation title={tooltipText} placement={placement}>
        <ItemText bold>{title}</ItemText>
      </TextTooltipInformation>
    );
  }

  return <ItemText bold>{title}</ItemText>;
};

export const OrderItem = ({
  title,
  value = '',
  secondValue = '',
  tooltipText,
  placement,
  error
}: OrderItemProps) => {
  return (
    <OrderItemWrapper>
      <OrderItemTitle {...{ title, tooltipText, placement }} />
      <ItemText error={error}>{value}</ItemText>
      {secondValue && <ItemText error={error}>{secondValue}</ItemText>}
    </OrderItemWrapper>
  );
};

const OrderDetails = ({
  details,
  updateCustomerId,
  receiverAddress,
  receiverDetails,
  summaryDetails
}: {
  details: OrderItemProps[];
  updateCustomerId: (param: string) => void;
  receiverAddress?: string;
  receiverDetails?: OrderItemProps[];
  summaryDetails?: OrderItemProps[];
}) => {
  const [purchaseNumbeState, setPurchaseNumbeState] = useState('');
  const minLength = 3;
  const updateCustomerPurchaseId = () => {
    if (purchaseNumbeState.length >= minLength) {
      updateCustomerId(purchaseNumbeState);
    }
  };

  const onChangeOrderNumber = (value: string) => {
    if (/^[a-zA-Z0-9--]*$/.test(value)) {
      setPurchaseNumbeState(value.slice(0, 34).trim());
    }
  };

  return (
    <div>
      <ItemText isTitle>Detalhes do pedido</ItemText>

      <OrderNameWrapper>
        <TextField
          label={'Identificação do pedido'}
          name={'orderName'}
          placeholder="Insira um nome para o seu pedido"
          value={purchaseNumbeState}
          onChange={(e) => onChangeOrderNumber(e.target.value)}
          onBlur={updateCustomerPurchaseId}
          helperText="Informação adicional e opcional para ajudá-lo a identificar pedido dentre os demais."
          supportMessage="Mínimo de três caracteres."
          variant={
            !purchaseNumbeState.length || purchaseNumbeState.length >= minLength
              ? undefined
              : 'danger'
          }
          helperTextTooltipPlacement="bottomLeft"
          maxLength={34}
        />
      </OrderNameWrapper>

      <ReceiverSectionTitle>Emissor do pedido</ReceiverSectionTitle>
      <OrderDetailsValuesContainer smallerMarginTop={true}>
        {details.map((detail) => (
          <OrderItem key={detail.title} {...detail} />
        ))}
      </OrderDetailsValuesContainer>

      <ReceiverSection>
        <ReceiverSectionTitle>Recebedor do pedido</ReceiverSectionTitle>
        {receiverAddress && (
          <ReceiverAddressBox>
            <ReceiverAddressLabel>Endereço do recebedor</ReceiverAddressLabel>
            <ReceiverAddressValue>{receiverAddress}</ReceiverAddressValue>
          </ReceiverAddressBox>
        )}
        {receiverDetails && receiverDetails.length > 0 && (
          <OrderDetailsValuesContainer smallerMarginTop={false}>
            {receiverDetails.map((detail) => (
              <OrderItem key={detail.title} {...detail} />
            ))}
          </OrderDetailsValuesContainer>
        )}
      </ReceiverSection>

      {summaryDetails && summaryDetails.length > 0 && (
        <OrderDetailsValuesContainer smallerMarginTop={false}>
          {summaryDetails.map((detail) => (
            <OrderItem key={detail.title} {...detail} />
          ))}
        </OrderDetailsValuesContainer>
      )}
    </div>
  );
};

const TaxItemRetry = ({
  title,
  value,
  error,
  tooltipText,
  placement,
  loading
}: OrderItemProps) => {
  return (
    <>
      {loading ? (
        <Skeleton width={270} height={48} />
      ) : (
        <OrderItem
          value={
            error || !value ? 'Erro ao carregar os valores de impostos.' : value
          }
          error={error || !value}
          title={title}
          tooltipText={tooltipText}
          placement={placement}
        />
      )}
    </>
  );
};

const TaxsContainer = ({
  onLoadTaxes,
  loading,
  retryCount
}: {
  onLoadTaxes: () => Promise<null | undefined>;
  loading: boolean;
  retryCount: number;
}) => {
  if (loading) {
    return (
      <SkeletonContainer>
        <Skeleton width={270} height={48} />
        <Skeleton width={270} height={48} />
        <Skeleton width={270} height={48} />
        <Skeleton width={270} height={48} />
        <Skeleton width={270} height={48} />
        <Skeleton width={270} height={48} />
        <Skeleton width={270} height={48} />
        <Skeleton width={270} height={48} />
      </SkeletonContainer>
    );
  }
  return (
    <ContainerErrorTaxs>
      <ContainerErrorTextTaxs>
        <SpanErrorTaxs>
          Ocorreu um erro ao carregar os valores de impostos.
        </SpanErrorTaxs>
        {retryCount < 4 && (
          <SpanRetryTaxs onClick={onLoadTaxes}>Tentar novamente</SpanRetryTaxs>
        )}
      </ContainerErrorTextTaxs>
      <SpanObsTaxs>
        Outras informações do seu pedido não foram afetadas.
      </SpanObsTaxs>
    </ContainerErrorTaxs>
  );
};

const OrderValues = ({
  values,
  taxs,
  orderPaymentDescription,
  onLoadTaxes,
  taxesData
}: {
  values: OrderItemProps[];
  taxs: OrderItemProps[] | null;
  orderPaymentDescription: string;
  onLoadTaxes: () => Promise<null | undefined>;
  taxesData: {
    loading: boolean;
    error: boolean;
    data: ITaxesCalculateResponse | undefined;
    retryCount: number;
  };
}) => {
  const newCartEndpoint = GetFlag('new_cart_endpoints');

  return (
    <OrderValuesContainer>
      <ItemText isTitle>Valores do pedido</ItemText>
      <OrderDetailsValuesContainer smallerMarginTop={false}>
        {[...(newCartEndpoint ? [values[0] || []] : values)].map((detail) => (
          <OrderItem
            key={detail.title}
            {...{
              ...detail
            }}
          />
        ))}
        {newCartEndpoint && (
          <>
            <TaxItemRetry
              {...{
                ...values[1],
                error: taxesData.error,
                loading: taxesData.loading
              }}
            />
            <OrderItem
              title="Condição de pagamento"
              value={orderPaymentDescription}
            />
          </>
        )}
      </OrderDetailsValuesContainer>
      <Divider />
      <ItemText>
        Valores de impostos inclusos no preço total do pedido.
      </ItemText>
      {newCartEndpoint && (!taxs || taxs.length === 0) ? (
        <TaxsContainer
          onLoadTaxes={onLoadTaxes}
          loading={taxesData.loading}
          retryCount={taxesData.retryCount}
        />
      ) : (
        <OrderDetailsValuesContainer smallerMarginTop={true}>
          {taxs?.map((detail) => (
            <OrderItem key={detail.title} {...{ ...detail }} />
          ))}
        </OrderDetailsValuesContainer>
      )}
    </OrderValuesContainer>
  );
};

const CheckoutOrder = ({
  orderDetails,
  receiverDetails,
  summaryDetails,
  orderValues,
  orderTax,
  updateCustomerId,
  orderPaymentDescription,
  onLoadTaxes,
  receiverAddress,
  taxesData
}: CheckoutOrderProps) => {
  return (
    <Container>
      <OrderDetails
        details={orderDetails}
        updateCustomerId={updateCustomerId}
        receiverAddress={receiverAddress}
        receiverDetails={receiverDetails}
        summaryDetails={summaryDetails}
      />
      <OrderValues
        values={orderValues}
        taxs={orderTax}
        orderPaymentDescription={orderPaymentDescription}
        onLoadTaxes={onLoadTaxes}
        taxesData={taxesData}
      />
    </Container>
  );
};

export default CheckoutOrder;
